import { type Dispatch, type SetStateAction, type FC, useState } from 'react';

import { Product } from '@eneco/flows/types';
import { useFormatter } from '@eneco/i18n';
import { Fields as OfferStepFields } from '@sitecore/types/OfferStep';

import { FlowConsumerContextEnergy } from '../../../context';
import { ExpandableOfferSection, Ribbon } from '../../../shared/components';
import {
  checkOfferIncludesDynamicPricingProduct,
  constructTextualDataKey,
  getCashbackTexts,
} from '../../../shared/helpers';
import { checkOnDiscountType } from '../../../shared/helpers/sitecore';
import { useProductItemContent } from '../../../shared/hooks';
import { EnergyType, Offer } from '../../../shared/types';
import { PriceDetailsOverview, ProductCard, TOURibbon } from '../../components';

export interface OfferCardProps {
  offer: Offer;
  energyType?: EnergyType;
  textualData?: OfferStepFields;
  flowContext?: FlowConsumerContextEnergy;
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  /** Whether this card is the selected radio option. Defaults to true. */
  isSelected?: boolean;
  /** Select this offer when tariff details are opened while the card is unselected. */
  onSelect?: () => void;
  /** The ProductCard component to use. Defaults to the shared ProductCard. */
  CardComponent?: typeof ProductCard;
  /** Extra props passed to the CardComponent. */
  extraCardProps?: Record<string, unknown>;
}

export const DefaultOfferCard: FC<OfferCardProps> = ({
  offer,
  energyType = EnergyType.Electricity,
  textualData,
  flowContext,
  isOpen,
  setOpen,
  isSelected,
  onSelect,
  CardComponent = ProductCard,
  extraCardProps,
}) => {
  const { format, currency } = useFormatter();
  const [costPeriod, setCostPeriod] = useState('costsMonthly');

  const textualDataKey = checkOnDiscountType(
    constructTextualDataKey(offer.contractSpan, energyType),
    offer?.discountType,
  );

  const { getProductDetails } = useProductItemContent(textualData?.productData?.productDataList);
  const productDetails = getProductDetails(textualDataKey);

  if (!productDetails) {
    return null;
  }

  const { title, promotionText, usps } = productDetails;

  const cashbackTexts = getCashbackTexts(
    offer?.costDiscounts?.cashback?.type,
    textualData?.promotionTexts,
    textualData?.giftData,
    offer?.costDiscounts?.cashback?.value,
  );

  const getCostPerPeriod = (data: Offer | Product) =>
    costPeriod === 'costsMonthly' ? data.costsMonthly : data.costsYearly;
  const isDynamicOffer = checkOfferIncludesDynamicPricingProduct(offer);

  const getPeriodLabel = () => {
    if (costPeriod === 'costsMonthly') {
      return isDynamicOffer
        ? textualData?.productData?.perMonthEstimatedLabel?.value
        : textualData?.productData?.perMonthLabel?.value;
    }
    return isDynamicOffer ? 'verwacht per jaar' : 'geschat per jaar';
  };

  return (
    <CardComponent
      id={offer.type ?? ''}
      title={title}
      tag={offer.mostPopularChoice ? promotionText : undefined}
      usps={usps}
      price={{
        value: getCostPerPeriod(offer)?.vatIncluded ?? 0,
        period: getPeriodLabel(),
      }}
      ribbon={
        cashbackTexts ? (
          <Ribbon
            size="small"
            emphasis="high"
            title={format(cashbackTexts.title, {
              [`${offer?.costDiscounts?.cashback?.type}`]: currency.euroNoFractionDigits(
                offer?.costDiscounts?.cashback?.value ?? 0,
              ),
            })}
            text={cashbackTexts.text}
            trigger={cashbackTexts?.trigger}
          />
        ) : null
      }
      touRibbon={<TOURibbon textualData={textualData} offer={offer} />}
      {...extraCardProps}>
      <ExpandableOfferSection
        isOpen={isOpen}
        setOpen={setOpen}
        isSelected={isSelected}
        onSelect={onSelect}
        offer={offer}
        textualData={textualData}
        flowContext={flowContext}
        setCostPeriod={setCostPeriod}
        textualDataKey={textualDataKey}
        showProductName={false}
        showEndOfContractDialog={false}>
        {flowContext && <PriceDetailsOverview flowContext={flowContext} textualData={textualData} offer={offer} />}
      </ExpandableOfferSection>
    </CardComponent>
  );
};
